import React, { useState } from 'react';
import { Form, Upload, Button, Image, message, Typography, Space } from 'antd';
import { UploadOutlined, DeleteOutlined, EyeOutlined } from '@ant-design/icons';
import masterService from '../../services/masterService';

const { Text } = Typography;

const ImageUpload = ({ 
  label, 
  name, 
  width, 
  height, 
  imageType = 'banners', 
  required = false,
  value,
  onChange 
}) => {
  const [imageUrl, setImageUrl] = useState(value || '');
  const [uploading, setUploading] = useState(false);

  // Handle file upload
  const handleUpload = async (file) => {
    const isImage = file.type.startsWith('image/');
    if (!isImage) {
      message.error('You can only upload image files!');
      return false;
    }

    const isLt10M = file.size / 1024 / 1024 < 10;
    if (!isLt10M) {
      message.error('Image must be smaller than 10MB!');
      return false;
    }

    try {
      setUploading(true);
      
      const formData = new FormData();
      formData.append('image_type', imageType);
      formData.append('image', file);

      const response = await masterService.uploadImage(formData);
      
      if (response.data && response.data.status) {
        const imagePath = response.data.data.image_path;
        setImageUrl(imagePath);
        onChange && onChange(imagePath);
        message.success('Image uploaded successfully!');
        return false; // Prevent default upload behavior
      } else {
        message.error('Failed to upload image');
        return false;
      }
    } catch (error) {
      console.error('Upload error:', error);
      message.error('Failed to upload image');
      return false;
    } finally {
      setUploading(false);
    }
  };

  // Handle image removal
  const handleRemove = async () => {
    if (!imageUrl) return;

    try {
      setUploading(true);
      
      const response = await masterService.removeImage({
        image_type: imageType,
        filename: imageUrl
      });

      if (response.data && response.data.status) {
        setImageUrl('');
        onChange && onChange('');
        message.success('Image removed successfully!');
      } else {
        message.error('Failed to remove image');
      }
    } catch (error) {
      console.error('Remove error:', error);
      message.error('Failed to remove image');
    } finally {
      setUploading(false);
    }
  };

  // Upload props
  const uploadProps = {
    name: 'image',
    listType: 'picture',
    showUploadList: false,
    beforeUpload: handleUpload,
    accept: 'image/*',
    disabled: uploading
  };

  return (
    <Form.Item
      label={label}
      name={name}
      rules={required ? [{ required: true, message: `Please upload ${label.toLowerCase()}` }] : []}
    >
      <div>
        {/* Upload Button */}
        <Upload {...uploadProps}>
          <Button 
            icon={<UploadOutlined />} 
            loading={uploading}
            disabled={uploading}
          >
            {uploading ? 'Uploading...' : 'Upload Image'}
          </Button>
        </Upload>

        {/* Image Dimensions Info */}
        <div style={{ marginTop: '8px' }}>
          <Text type="secondary" style={{ fontSize: '12px' }}>
            Recommended size: {width}px (W) × {height}px (H)
          </Text>
        </div>

        {/* Image Preview */}
        {imageUrl && (
          <div style={{ marginTop: '16px' }}>
            <div style={{ 
              border: '1px solid #d9d9d9', 
              borderRadius: '6px', 
              padding: '8px',
              display: 'inline-block'
            }}>
              <Image
                src={imageUrl}
                alt="Preview"
                style={{ 
                  maxWidth: '200px', 
                  maxHeight: '150px',
                  objectFit: 'contain'
                }}
                preview={{
                  mask: <EyeOutlined />
                }}
              />
            </div>
            
            <div style={{ marginTop: '8px' }}>
              <Space>
                <Button
                  type="link"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={handleRemove}
                  loading={uploading}
                  disabled={uploading}
                  size="small"
                >
                  Remove
                </Button>
              </Space>
            </div>
          </div>
        )}
      </div>
    </Form.Item>
  );
};

export default ImageUpload;